import type { StartTimeoutBuckets } from '@evi/shared';
import { formatTimeoutSummary, offlineLabel } from '@/hooks/useStartTimeouts';
import styles from './TimeoutCell.module.css';

/**
 * The start-timeout number as a table cell — the same string
 * everywhere ("9 of 12 starts · 75%") and a red flag pill when the
 * row is over the threshold (above 40% of ten or more accepted starts;
 * the server decides, the cell only renders). "—" when nothing is
 * known yet, "no starts" when the window had none.
 */
type CellRow = StartTimeoutBuckets & { online?: boolean; offline_since?: string | null };

export function TimeoutCell({ row, compact }: { row: CellRow | undefined; compact?: boolean }) {
  if (!row) return <span className={styles.dim}>—</span>;
  // A dark charger has no starts and so no rate — the failure that runs
  // longest would read "no starts". Say what it is instead.
  if (row.online === false && row.starts === 0) {
    return (
      <span className={styles.cell} title={tooltip(row)}>
        <span className={styles.flag}>Offline</span>
        <span className={styles.flaggedText}>{offlineLabel(row.offline_since ?? null)}</span>
      </span>
    );
  }
  const label = formatTimeoutSummary(row);
  // The diagnosis ("cable in another pedestal N of M") lives in the
  // tooltip and on the detail pages — a table cell stays short.
  return (
    <span className={styles.cell} title={tooltip(row)}>
      {row.online === false && <span className={styles.flag}>Offline</span>}
      {row.flagged && <span className={styles.flag}>Timeouts</span>}
      <span className={row.flagged ? styles.flaggedText : styles.dim}>
        {compact && row.timeout_rate !== null ? `${Math.round(row.timeout_rate * 100)}%` : label}
      </span>
    </span>
  );
}

function tooltip(row: CellRow): string {
  const parts = [
    row.online === false ? offlineLabel(row.offline_since ?? null) : null,
    `${row.charged} charged`,
    `${row.timed_out} timed out`,
    row.sibling_cable > 0 ? `${row.sibling_cable} with the cable in another pedestal` : null,
    row.late_cancel > 0 ? `${row.late_cancel} cancelled after the window` : null,
    row.cancelled > 0 ? `${row.cancelled} cancelled` : null,
    row.rejected > 0 ? `${row.rejected} not accepted (charger offline or refused — not counted)` : null,
    row.pending > 0 ? `${row.pending} still pending` : null,
  ].filter((p): p is string => p !== null);
  return parts.join(' · ');
}
